import AirdropPoolAbi from "@/contracts/abis/AirdropPool.json";
import SponAirdropAbi from "@/contracts/abis/SPONAirdrop.json";
import SponVestingAbi from "@/contracts/abis/SPONVesting.json";
import ThawingPoolAbi from "@/contracts/abis/ThawingPool.json";
import { ethers } from "ethers";

export type ClaimResult =
  | { success: true }
  | { success: false; message: string };

export const DEFAULT_CLAIM_ERROR = "Something went wrong. Could not claim SPON.";

export const CLAIM_PERIOD_ENDED_ERROR =
  "The claim period has ended, so SPON can no longer be claimed on-chain.";

const KNOWN_ABIS = [
  SponVestingAbi,
  SponAirdropAbi,
  AirdropPoolAbi,
  ThawingPoolAbi,
] as ethers.InterfaceAbi[];

/**
 * Selector (first 4 bytes of the error signature hash) -> error name, built from
 * every ABI we ship. Solidity custom errors are only 4 bytes on the wire, so
 * without this lookup a revert surfaces as an undecodable blob such as
 * "0xd322d629".
 */
const ERRORS_BY_SELECTOR = KNOWN_ABIS.reduce<Record<string, string>>(
  (acc, abi) => {
    const iface = new ethers.Interface(abi);
    iface.forEachError((fragment) => {
      acc[fragment.selector.toLowerCase()] = fragment.name;
    });
    return acc;
  },
  {
    // Solidity built-ins, not part of any ABI.
    "0x08c379a0": "Error",
    "0x4e487b71": "Panic",
  }
);

/**
 * Human readable copy for the reverts a claiming user can realistically hit.
 * Anything not listed here falls back to the raw error name so support still
 * gets something actionable instead of "something went wrong".
 */
const ERROR_MESSAGES: Record<string, string> = {
  // SPONVesting
  ClaimPeriodHasEnded:
    "The claim window for the vesting contract has closed, so vested SPON can no longer be claimed on-chain.",
  ClaimingManualReviewAddress:
    "This wallet is flagged for manual review, so claiming is paused for it.",
  InvalidMerkleProof:
    "Your claim proof was rejected by the contract. Please refresh the page and try again.",
  ProofRequiredForFirstClaim:
    "Your claim proof is missing. Please refresh the page and try again.",
  NotInitialized:
    "Your vesting schedule has not been initialized yet. Please try again later.",
  InvalidAmount: "The claim amount is invalid. Please refresh and try again.",

  // SPONAirdrop
  AirdropClaimPeriodEnded:
    "The airdrop claim window has closed, so SPON can no longer be claimed on-chain.",
  AirdropAlreadyClaimed: "This wallet has already claimed its SPON airdrop.",
  AirdropNotLaunched:
    "The airdrop has not launched yet. Please try again after launch.",
  AirdropInvalidProof:
    "Your claim proof was rejected by the contract. Please refresh the page and try again.",
  AirdropAddressManualReview:
    "This wallet is flagged for manual review, so claiming is paused for it.",
  AirdropInvalidAmount:
    "The claim amount is invalid. Please refresh and try again.",

  // Shared / OpenZeppelin
  EnforcedPause:
    "Claiming is currently paused on the contract. Please try again later.",
  InsufficientBalance:
    "The contract does not hold enough SPON to cover this claim.",
  SafeERC20FailedOperation: "The SPON token transfer failed.",
};

const USER_REJECTED_MESSAGE = "You rejected the transaction in your wallet.";

const HEX_DATA_PATTERN = /0x[0-9a-fA-F]{8,}/g;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Wallet / provider / thirdweb errors nest the revert data at wildly different
 * paths, and viem's AbiErrorSignatureNotFoundError only leaves the selector in
 * the message string. Walk the whole error graph and collect every hex blob
 * that could be revert data.
 */
const collectHexCandidates = (
  error: unknown,
  seen = new Set<unknown>(),
  depth = 0
): string[] => {
  if (depth > 6 || error === undefined || error === null) return [];

  if (typeof error === "string") {
    return error.match(HEX_DATA_PATTERN) ?? [];
  }

  if (!isRecord(error) || seen.has(error)) return [];
  seen.add(error);

  const candidates: string[] = [];

  for (const value of Object.values(error)) {
    candidates.push(...collectHexCandidates(value, seen, depth + 1));
  }

  return candidates;
};

export interface DecodedContractError {
  name: string;
  args?: readonly unknown[];
}

/** Decodes a Solidity revert (custom error, require string or panic) if we can. */
export const decodeContractError = (
  error: unknown
): DecodedContractError | null => {
  for (const candidate of collectHexCandidates(error)) {
    const selector = candidate.slice(0, 10).toLowerCase();
    const name = ERRORS_BY_SELECTOR[selector];
    if (!name) continue;

    for (const abi of KNOWN_ABIS) {
      try {
        const parsed = new ethers.Interface(abi).parseError(candidate);
        if (parsed) return { args: parsed.args, name: parsed.name };
      } catch {
        // Candidate isn't decodable against this ABI - fall through to the
        // selector-only match below.
      }
    }

    return { name };
  }

  return null;
};

const isUserRejection = (error: unknown): boolean => {
  if (!isRecord(error)) return false;
  const code = error.code;
  if (code === 4001 || code === "ACTION_REJECTED") return true;
  const message = typeof error.message === "string" ? error.message : "";
  return /user (rejected|denied)|rejected the request/i.test(message);
};

/**
 * Turns a failed claim transaction into a message we can show the user, instead
 * of leaking `AbiErrorSignatureNotFoundError` to the console and nothing to the
 * UI.
 */
export const getClaimErrorMessage = (
  error: unknown,
  fallback: string = DEFAULT_CLAIM_ERROR
): string => {
  if (isUserRejection(error)) return USER_REJECTED_MESSAGE;

  const decoded = decodeContractError(error);
  if (!decoded) return fallback;

  if (decoded.name === "Error") {
    const reason = decoded.args?.[0];
    return typeof reason === "string" && reason ? reason : fallback;
  }

  if (decoded.name === "Panic") return fallback;

  return ERROR_MESSAGES[decoded.name] ?? `${fallback} (${decoded.name})`;
};
